import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { ChatIcon } from '@/components/ui/Icons';
import { presentApiError } from '@/lib/form-errors';
import { useStartConversation } from './use-messaging';

/**
 * "Message" — the entry point into a thread from wherever the person appears.
 *
 * Opening is idempotent server-side, so this always lands in the one thread for
 * this job and this pair rather than creating a second. A refusal is shown
 * inline, because the reason is usually meaningful ("they have not applied yet")
 * rather than a system failure.
 */
export function MessageButton({
  jobId,
  personId,
  label = 'Message',
  variant = 'outline',
}: {
  /** The job's publicId — every thread hangs off a job. */
  jobId: string;
  /** Whatever publicId this screen holds for the person: teen, parent, poster. */
  personId: string;
  label?: string;
  variant?: 'primary' | 'outline' | 'ghost';
}) {
  const navigate = useNavigate();
  const start = useStartConversation();
  const [error, setError] = useState<string | null>(null);

  async function open(): Promise<void> {
    setError(null);
    try {
      const conversation = await start.mutateAsync({ jobId, withPersonId: personId });
      void navigate(`/messages/${conversation.id}`);
    } catch (caught) {
      setError(presentApiError(caught, 'Could not open that conversation').message);
    }
  }

  return (
    <span className="inline-flex flex-col items-start gap-1">
      <Button variant={variant} disabled={start.isPending} onClick={() => void open()}>
        <ChatIcon className="mr-1.5 size-4" />
        {start.isPending ? 'Opening…' : label}
      </Button>
      {error && <span className="text-xs font-medium text-red-600">{error}</span>}
    </span>
  );
}
